import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DisionLogo from './DisionLogo';

const developmentLinks = [
  { label: 'Software Product Development', href: '/development/software-product-development' },
  { label: 'Web Application Development', href: '/development/web-application-development' },
  { label: 'DevNetOps Services', href: '/development/dev-net-ops-services' },
  { label: 'IT Consulting Services', href: '/development/it-consulting-services' },
];

const servicesLinks = [
  { label: 'Network Security', href: '/services/network-security' },
  { label: 'Cybersecurity Assessment', href: '/services/cybersecurity' },
  { label: 'Cloud Infrastructure', href: '/services/cloud-infrastructure' },
  { label: 'UI/UX Design', href: '/services/ui-ux-design' },
  { label: 'Data Analytics', href: '/services/data-analytics' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [devOpen, setDevOpen] = useState(false);
  const [servOpen, setServOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileDevOpen, setMobileDevOpen] = useState(false);
  const [mobileServOpen, setMobileServOpen] = useState(false);
  const devRef = useRef(null);
  const servRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (devRef.current && !devRef.current.contains(e.target)) setDevOpen(false);
      if (servRef.current && !servRef.current.contains(e.target)) setServOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleNav = (href) => {
    setMobileOpen(false);
    setDevOpen(false);
    setServOpen(false);
    navigate(href);
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999,
        background: 'white',
        borderBottom: scrolled ? '1px solid #e5e7eb' : '1px solid #f3f4f6',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Logo */}
        <button onClick={() => { handleNav('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <DisionLogo height={28} dark={false} />
        </button>

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="desktop-nav">
          {/* Development dropdown */}
          <div ref={devRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setDevOpen(!devOpen); setServOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '8px 14px', border: 'none', background: 'none',
                color: '#374151', fontSize: '15px', fontWeight: '500',
                cursor: 'pointer', borderRadius: '6px',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Development <ChevronDown size={16} style={{ transition: 'transform 0.2s', transform: devOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
            </button>
            {devOpen && (
              <div className="nav-dropdown">
                {developmentLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNav(link.href)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', fontSize: '14px', transition: 'background-color 0.15s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#111827'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Services dropdown */}
          <div ref={servRef} style={{ position: 'relative' }}>
            <button
              onClick={() => { setServOpen(!servOpen); setDevOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '8px 14px', border: 'none', background: 'none',
                color: '#374151', fontSize: '15px', fontWeight: '500',
                cursor: 'pointer', borderRadius: '6px',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Services <ChevronDown size={16} style={{ transition: 'transform 0.2s', transform: servOpen ? 'rotate(180deg)' : 'rotate(0)' }} />
            </button>
            {servOpen && (
              <div className="nav-dropdown">
                {servicesLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNav(link.href)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer', color: '#374151', fontSize: '14px', transition: 'background-color 0.15s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#111827'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {[{ label: 'News', href: '/news' }, { label: 'Jobs', href: '/jobs' }, { label: 'About us', href: '/about-us' }].map(item => (
            <button
              key={item.label}
              onClick={() => handleNav(item.href)}
              style={{
                padding: '8px 14px', color: '#374151', background: 'none', border: 'none',
                fontSize: '15px', fontWeight: '500', borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* Contact Button */}
        <button
          onClick={() => handleNav('/about-us')}
          style={{
            backgroundColor: '#F97316',
            color: 'white',
            padding: '10px 22px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EA580C'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#F97316'}
        >
          Contact us
        </button>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: '8px' }}
          className="mobile-menu-btn"
        >
          {mobileOpen ? <X size={24} color="#374151" /> : <Menu size={24} color="#374151" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div style={{ background: 'white', borderTop: '1px solid #e5e7eb', padding: '16px 24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button
              onClick={() => setMobileDevOpen(!mobileDevOpen)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '500', color: '#111827', borderBottom: '1px solid #f3f4f6' }}
            >
              Development <ChevronDown size={16} style={{ transform: mobileDevOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileDevOpen && developmentLinks.map(l => (
              <button key={l.label} onClick={() => handleNav(l.href)} style={{ padding: '10px 16px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', textAlign: 'left' }}>{l.label}</button>
            ))}
            <button
              onClick={() => setMobileServOpen(!mobileServOpen)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '500', color: '#111827', borderBottom: '1px solid #f3f4f6' }}
            >
              Services <ChevronDown size={16} style={{ transform: mobileServOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            {mobileServOpen && servicesLinks.map(l => (
              <button key={l.label} onClick={() => handleNav(l.href)} style={{ padding: '10px 16px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', textAlign: 'left' }}>{l.label}</button>
            ))}
            {[{ label: 'News', href: '/news' }, { label: 'Jobs', href: '/jobs' }, { label: 'About us', href: '/about-us' }].map(item => (
              <button key={item.label} onClick={() => handleNav(item.href)} style={{ padding: '12px 0', color: '#111827', background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '500', borderBottom: '1px solid #f3f4f6', textAlign: 'left' }}>{item.label}</button>
            ))}
            <button onClick={() => handleNav('/about-us')} style={{ marginTop: '12px', backgroundColor: '#F97316', color: 'white', padding: '12px 24px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '15px', fontWeight: '600', textAlign: 'center' }}>Contact us</button>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}
